// "What's next": up to three things for where this customer is, from
// GET /me/next, with where they are on the way from first chat to covered.
import { Link } from 'react-router-dom';
import { ArrowRight, MessageCircle } from 'lucide-react';
import type { NextAction, NextActions } from '@cover/core/api/types.ts';
import { useChat } from './Chat.tsx';
import { cn } from '@cover/core/cn.ts';
import { Card, CardBody, CardHeader } from '@cover/core/ui/Card.tsx';
import { useNext } from '../api.ts';

const STAGES: { id: NextActions['stage']; label: string }[] = [
  { id: 'new', label: 'Say hello' },
  { id: 'profile', label: 'Your business' },
  { id: 'ready_to_quote', label: 'Get a price' },
  { id: 'quoted', label: 'Buy' },
  { id: 'covered', label: 'Covered' },
];

const ROUTES = ['/claims', '/equipment', '/policies', '/account', '/quotes', '/business', '/documents', '/connections', '/refer'];

/**
 * The engine's href as a path in this app. Hrefs are absolute (the brand's
 * dashboard URL), so this also works on another host for the same app, such
 * as the workers.dev address.
 */
function inApp(href: string | undefined): string | null {
  if (!href) return null;
  try {
    const u = new URL(href, window.location.origin);
    return ROUTES.some((r) => u.pathname === r || u.pathname.startsWith(`${r}/`)) ? u.pathname + u.search : null;
  } catch {
    return null;
  }
}

function Cta({ a, primary }: { a: NextAction; primary: boolean }) {
  const chat = useChat();
  const cls = cn(
    'inline-flex min-h-11 items-center gap-2 rounded-control px-4 text-sm font-semibold no-underline hover:no-underline',
    primary ? 'bg-brand text-on-brand hover:brightness-110' : 'bg-white text-ink ring-1 ring-inset ring-line-strong hover:bg-surface',
  );
  if (a.cta.action === 'chat')
    return (
      <button type="button" onClick={() => chat.open()} className={cls}>
        <MessageCircle className="h-4 w-4" aria-hidden /> {a.cta.label}
      </button>
    );
  const path = inApp(a.cta.href);
  if (path)
    return (
      <Link to={path} className={cls}>
        {a.cta.label} <ArrowRight className="h-4 w-4" aria-hidden />
      </Link>
    );
  return a.cta.href ? (
    <a href={a.cta.href} className={cls}>
      {a.cta.label} <ArrowRight className="h-4 w-4" aria-hidden />
    </a>
  ) : null;
}

export function WhatsNext() {
  const next = useNext();
  if (!next.data || !next.data.actions.length) return null;
  const { stage, actions } = next.data;
  const at = STAGES.findIndex((s) => s.id === stage);
  return (
    <Card>
      <CardHeader title="What’s next" />
      <CardBody className="space-y-5 pt-3">
        <ol className="flex items-center gap-1.5" aria-label="Where you are">
          {STAGES.map((s, i) => (
            <li key={s.id} className="flex flex-1 flex-col gap-1.5" aria-current={i === at ? 'step' : undefined}>
              <span className={cn('h-1.5 rounded-full', i <= at ? 'bg-brand' : 'bg-line')} aria-hidden />
              <span className={cn('hidden text-xs sm:block', i === at ? 'font-semibold text-ink' : 'text-muted')}>
                {s.label}
                <span className="sr-only">{i < at ? ', done' : i === at ? ', you are here' : ''}</span>
              </span>
            </li>
          ))}
        </ol>
        <ul className="space-y-3">
          {actions.map((a, i) => (
            <li key={a.id} className={cn('flex flex-wrap items-center justify-between gap-3 rounded-card p-4', i === 0 ? 'bg-brand/[0.06] ring-1 ring-inset ring-brand/20' : 'ring-1 ring-inset ring-line')}>
              <div className="min-w-0 flex-1 basis-60">
                <p className="font-semibold">{a.title}</p>
                <p className="text-sm text-mid">{a.body}</p>
              </div>
              <Cta a={a} primary={i === 0} />
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}
